import { SchoolLocale } from "@app/locales/localeid";
import { SearchService } from "@app/service/school/search";
import {
    ContextHelper,
    fmtMsg,
    htmlEncodeByRegExp,
    regExpEscape,
} from "@app/util";
import { AutoComplete, Empty, Icon, Input, Select } from "antd-min";
import {
    alignPop,
    GLGlobal,
    GLLocale,
    HeaderIcon,
    RegionService,
    ResourceType,
    RoleName,
    StateType,
    getProcessEnv,
} from "gl-commonui";
import React, { Component } from "react";
import { connect } from "react-redux";

import "./header-search.less";

interface HeaderSearchState {
    onQuery?: boolean;
    dataSource?: any[];
    searchResult?: JSX.Element[];
    cancelSearch?: boolean;
    canSearch?: boolean;
    hidePlaceHolder: boolean;
    availableRegions?: Array<{ id: string; name: string; englishName: string; tenantId: number }>;
    selectedRegion?: string;
}

enum SearchResultCategory {
    School = "School",
    Campus = "Campus",
    SchoolClass = "Class",
    Region = "Region",
    Order = "Order",
    User = "User",
    Student = "Student",
    //Parent = "Parent",
    //Content = 'Content',
    //LessonVideo = 'Lesson/Video',
}

const searchCategoryIntlMap = new Map<string, string>([
    [SearchResultCategory.Region, SchoolLocale.SearchRegions],
    [SearchResultCategory.School, SchoolLocale.SearchSchools],
    [SearchResultCategory.Campus, SchoolLocale.SearchCampuses],
    [SearchResultCategory.SchoolClass, SchoolLocale.SearchClasses],
    [SearchResultCategory.User, SchoolLocale.SearchUsers],
    // [SearchResultCategory.Parent, SchoolLocale.SearchParents],
    [SearchResultCategory.Student, SchoolLocale.SearchStudents],
    [SearchResultCategory.Order, SchoolLocale.SearchOrders],
    // ['Content', SchoolLocale.SearchContents],
    // ['Lesson/Video', SchoolLocale.SearchLessonVideo],
]);

@connect(({ oidc: { loginInfo }, tenant }: StateType) => ({
    actionLoaded:
        loginInfo &&
        loginInfo.loggedin &&
        loginInfo.profile &&
        loginInfo.profile.actions &&
        true,
    loginInfo: loginInfo,
    tenantConfig: tenant && tenant.config,
}))
export class HeaderSearch extends Component<any, HeaderSearchState> {
    private searchService: SearchService;
    private searchId;
    private searchElement;
    private roleText: Map<RoleName, string>;
    private nonClickableClicked: boolean = false;
    private searchContainerRef: HTMLDivElement | null = null;
    private regionSearch;

    constructor(props) {
        super(props);
        this.searchService = new SearchService();
        this.searchId = null;
        this.roleText = ContextHelper.getRoleTextMap();

        // Load available regions from user profile
        const loginInfo = GLGlobal.loginInfo();
        const availableRegions =
            (loginInfo && loginInfo.profile && loginInfo.profile.regionInfo) ||
            [];

        this.regionSearch =
            availableRegions.length > 0 ? availableRegions[0].id : null;

        this.state = {
            onQuery: false,
            dataSource: [],
            searchResult: null,
            cancelSearch: false,
            canSearch: false,
            hidePlaceHolder: false,
            availableRegions,
            selectedRegion: availableRegions.length > 0 ? availableRegions[0].id : undefined,
        };
        this.beginSearch = this.beginSearch.bind(this);
        this.endSearch = this.endSearch.bind(this);
        this.onChange = this.onChange.bind(this);
        this.onSearch = this.onSearch.bind(this);
        this.onSelect = this.onSelect.bind(this);
        this.onMouseUp = this.onMouseUp.bind(this);
        this.search = this.search.bind(this);
        this.getSearchResultElements = this.getSearchResultElements.bind(this);
        this.getHighlightedText = this.getHighlightedText.bind(this);
        this.renderItemTitle = this.renderItemTitle.bind(this);
        this.renderHitItemContent = this.renderHitItemContent.bind(this);
        this.getRequiredResult = this.getRequiredResult.bind(this);
    }

    componentDidMount() {
        document.addEventListener("mousedown", this.handleClickOutside);
        this.getAccessibleRegions();
    }

    componentDidUpdate(prevProps: any) {
        // Update availableRegions when loginInfo changes (after login or profile load)
        if (
            this.props.loginInfo &&
            this.props.loginInfo !== prevProps.loginInfo &&
            this.props.loginInfo.profile &&
            this.props.loginInfo.profile.regionInfo
        ) {
            this.getAccessibleRegions();
        }

        // Refetch regionInfo when tenant context changes (URL may now include/exclude tenant routing)
        var prevKey = prevProps.tenantConfig && prevProps.tenantConfig.resolvedKey;
        var currKey = this.props.tenantConfig && this.props.tenantConfig.resolvedKey;
        if (
            currKey && currKey !== prevKey &&
            this.props.loginInfo &&
            this.props.loginInfo.profile &&
            this.props.loginInfo.profile.userRoleResources
        ) {
            this.refetchRegionInfo();
        }
    }

    async getAccessibleRegions() {
        const regionInfo =
            (this.props.loginInfo &&
                this.props.loginInfo.profile &&
                this.props.loginInfo.profile.regionInfo) ||
            (GLGlobal.loginInfo() &&
                GLGlobal.loginInfo().profile &&
                GLGlobal.loginInfo().profile.regionInfo) ||
            [];

        // Set default region if not already set and regions are available
        if (regionInfo.length > 0 && !this.regionSearch) {
            this.regionSearch = regionInfo[0].id;
        }

        this.setState({
            availableRegions: regionInfo,
            selectedRegion: this.regionSearch || (regionInfo.length > 0 ? regionInfo[0].id : undefined),
        });
    }

    private refetchRegionInfo() {
        var loginInfo = this.props.loginInfo || GLGlobal.loginInfo();
        if (!loginInfo || !loginInfo.profile || !loginInfo.profile.userRoleResources) {
            return;
        }

        var internalIds = loginInfo.profile.userRoleResources.map(function (i) { return i.regionInternalId; });
        var uniqueInternalIds = internalIds.filter(function (id, idx, arr) { return arr.indexOf(id) === idx; });

        var self = this;
        var regionService = new RegionService();
        regionService.getRegionInfo(uniqueInternalIds).then(function (regionInfo) {
            if (regionInfo.length > 0 && !self.regionSearch) {
                self.regionSearch = regionInfo[0].id;
            }

            self.setState({
                availableRegions: regionInfo,
                selectedRegion: self.regionSearch || (regionInfo.length > 0 ? regionInfo[0].id : undefined),
            });
        }).catch(function (e) {
            console.error('Failed to refetch region info', e);
        });
    }

    componentWillUnmount() {
        document.removeEventListener("mousedown", this.handleClickOutside);
    }
    
    beginSearch(e) {
        e.preventDefault();
        this.setState({
            canSearch: true,
        });
    }

    endSearch(_e) {
        this.setState({
            dataSource: [],
            searchResult: null,
            cancelSearch: true,
        });
        if (
            this.searchElement &&
            this.searchElement.props &&
            this.searchElement.props.value
        ) {
            return;
        }
        this.setState({
            canSearch: false,
        });
    }

    handleClickOutside = (event: MouseEvent) => {
        if (
            this.searchContainerRef &&
            !this.searchContainerRef.contains(event.target as Node)
        ) {
            this.endSearch(event);
        }
    };

    onSelectRegion = (value) => {
        this.regionSearch = value;
        this.setState({ selectedRegion: value });
    };

    onChange(value) {
        this.setState({
            dataSource: [],
            searchResult: null,
            cancelSearch: true,
            hidePlaceHolder: this.nonClickableClicked,
        });
        this.nonClickableClicked = false;
    }

    onMouseUp(e) {
        this.searchElement &&
            this.searchElement.props &&
            this.searchElement.props.value &&
            this.onSearch(this.searchElement.props.value, e);
    }

    onSearch(value, e) {
        e.preventDefault();
        const keyword = value.trim();
        if (!keyword || keyword.length < 2) return;
        var self = this;
        clearTimeout(this.searchId);
        this.searchId = setTimeout(() => self.search(keyword), 20);
        this.setState({ cancelSearch: false });
    }

    onSelect(value, option) {
        if (!value) {
            if (option.props.className === "pending-invite-result") {
                this.nonClickableClicked = true;
                this.setState({ hidePlaceHolder: true });
            }
            return;
        }
        window.open(value, "_self");
    }

    search(keyword) {
        try {
            this.setState({ onQuery: true });
            const params: any = { keyword };

            // Override routing region for search requests
            if (this.regionSearch && this.regionSearch !== "global") {
                params.__gl_region__ = this.regionSearch;
                params.regionIds = [this.regionSearch];
            } else {
                params.__gl_region__ = "";
            }

            const searchRequest = [this.searchService.searchStructure(params)];

            Promise.all(searchRequest).then((data) => {
                const schoolSiteData = data[0];
                this.state.cancelSearch &&
                    this.setState({
                        onQuery: false,
                        dataSource: [],
                        searchResult: null,
                    });
                !this.state.cancelSearch &&
                    this.setState({
                        onQuery: false,
                        dataSource: data,
                        searchResult: this.getSearchResultElements(
                            schoolSiteData,
                            keyword
                        ),
                    });
            });
        } catch (error) {
            console.log(error);
            this.setState({ onQuery: false });
        }
    }

    getRequiredResult(item, searchText) {
        if (item.isCategoryLinksVisible) {
            return item.hits.map((opt, index) =>
                this.renderHitItemContent(item.category, opt, searchText, index)
            );
        } else {
            return (
                <AutoComplete.Option
                    key={item.category + "empty"}
                    value={item.category + "empty"}
                    title=""
                    disabled={true}
                >
                    {fmtMsg({ id: SchoolLocale.UserSearchMessage })}
                </AutoComplete.Option>
            );
        }
    }

    getSearchResultElements(
        resultData?: any[],
        searchText?: string
    ): JSX.Element[] {
        const elements = resultData.map((item) => {
            return (
                <AutoComplete.OptGroup
                    key={item.category}
                    label={this.renderItemTitle(item)}
                >
                    {this.getRequiredResult(item, searchText)}
                </AutoComplete.OptGroup>
            );
        });
        if (elements.length == 0) {
            return [
                <AutoComplete.Option
                    key={"empty"}
                    value={"empty"}
                    title=""
                    disabled={true}
                >
                    <Empty key={"empty"} image={Empty.PRESENTED_IMAGE_SIMPLE} />
                </AutoComplete.Option>,
            ];
        }
        return elements;
    }

    getHighlightedText(
        text: string,
        keyword: string,
        pending: boolean = false
    ) {
        let highlighted = text;
        if (keyword && text) {
            const regExp = new RegExp(regExpEscape(keyword), "i");
            const parts = text.split(regExp);
            let result = "",
                charIndex = 0;
            for (let index = 0; index < parts.length; index++) {
                result += `<span>${htmlEncodeByRegExp(parts[index])}</span>`;
                charIndex += parts[index].length;
                if (index < parts.length - 1) {
                    let orginalKeywordText = text.substr(
                        charIndex,
                        keyword.length
                    );
                    result += `<mark class='search-mark'>${htmlEncodeByRegExp(
                        orginalKeywordText
                    )}</mark>`;
                }
                charIndex += keyword.length;
            }
            return `<span>${result} ${
                pending ? fmtMsg({ id: SchoolLocale.AdminsPending }) : ""
            }</span>`;
        }
        return highlighted;
    }

    renderItemTitle(item): JSX.Element {
        const title = fmtMsg({ id: searchCategoryIntlMap.get(item.category) });
        return <span>{`${title} (${item.hits.length})`}</span>;
    }

    renderHitItemContent(
        category,
        hitItem,
        searchText,
        index: number
    ): JSX.Element {
        const text =
            hitItem.links && hitItem.links.length > 0
                ? hitItem.links[0].title
                : hitItem.hit;
        const href =
            hitItem.links && hitItem.links.length > 0
                ? hitItem.links[0].url
                : null;
        const description = this.buildDescription(category, hitItem);
        const pendingInvite =
            hitItem.extraInfo && hitItem.extraInfo.pendingInvite;

        return (
            <AutoComplete.Option
                key={hitItem.id ? hitItem.id : index}
                value={href}
                title={text}
                className={pendingInvite ? "pending-invite-result" : ""}
            >
                {href && (
                    <div className="item">
                        <a
                            className="item-link"
                            href={href}
                            target="_self"
                            rel="noopener noreferrer"
                        >
                            <div
                                dangerouslySetInnerHTML={{
                                    __html: this.getHighlightedText(
                                        text,
                                        searchText
                                    ),
                                }}
                            />
                        </a>
                        {description && (
                            <p className="description" title={description}>
                                {description}
                            </p>
                        )}
                    </div>
                )}
                {pendingInvite && (
                    <div className="item">
                        <div
                            dangerouslySetInnerHTML={{
                                __html: this.getHighlightedText(
                                    text,
                                    searchText,
                                    true
                                ),
                            }}
                        />
                        {description && (
                            <p className="description" title={description}>
                                {description}
                            </p>
                        )}
                    </div>
                )}
                {!href && !pendingInvite && text}
            </AutoComplete.Option>
        );
    }

    getUserRoleNames(roles: RoleName[]) {
        return roles.map((roleName) => {
            return fmtMsg({ id: this.roleText.get(roleName) });
        });
    }

    buildDescription(category, hitItem) {
        if (
            !hitItem.extraInfo ||
            hitItem.extraInfo.resourceType == ResourceType.Region
        )
            return null;
        const extraInfo = hitItem.extraInfo;
        const type = fmtMsg({ id: SchoolLocale.SearchResultUserType });
        let descriptions = [];
        switch (category) {
            case SearchResultCategory.Order:
                descriptions.push(
                    ...[
                        extraInfo.regionName,
                        extraInfo.schoolName,
                        extraInfo.campusName,
                    ]
                );
                break;
            case SearchResultCategory.User:
                descriptions.push(
                    `${type}: ${
                        extraInfo.roles
                            ? this.getUserRoleNames(
                                  ContextHelper.sortUserRoles(extraInfo.roles)
                              ).join(", ")
                            : ""
                    }`
                );
                break;
            case SearchResultCategory.Student:
                descriptions.push(
                    ...[
                        extraInfo.regionName,
                        extraInfo.schoolName,
                        extraInfo.campusName,
                        extraInfo.schoolClassName,
                    ]
                );
                break;
            default:
                extraInfo.resourceType != ResourceType.Region &&
                    extraInfo.region &&
                    descriptions.push(extraInfo.region.name);
                extraInfo.resourceType != ResourceType.School &&
                    extraInfo.school &&
                    descriptions.push(extraInfo.school.name);
                extraInfo.resourceType != ResourceType.Campus &&
                    extraInfo.campus &&
                    descriptions.push(extraInfo.campus.name);
                extraInfo.resourceType != ResourceType.SchoolClass &&
                    extraInfo.schoolClass &&
                    descriptions.push(extraInfo.schoolClass.name);
                break;
        }
        return `( ${descriptions.join(" > ")} )`;
    }

    render() {
        const canSearch = this.state.canSearch;
        const placeHolder = this.state.hidePlaceHolder
            ? " "
            : fmtMsg({ id: GLLocale.Search });
        const iconType = this.state.onQuery ? "loading" : "search";
        const { availableRegions = [] } = this.state;

        if (
            !this.props.actionLoaded ||
            ContextHelper.userIsParent(this.props.actionLoaded)
        )
            return null;
        return canSearch ? (
            <div
                ref={(el) => (this.searchContainerRef = el)}
                className="search-container include-region-select"
            >
                <Input.Group compact style={{ borderRadius: "12px" }}>
                    {availableRegions.length > 1 && (
                        <Select
                            className="region-select"
                            showSearch
                            getPopupContainer={() =>
                                this.searchContainerRef || document.body
                            }
                            style={{ width: 110 }}
                            dropdownMatchSelectWidth={false}
                            placeholder="Region"
                            onChange={this.onSelectRegion}
                            value={this.state.selectedRegion}
                            optionFilterProp="children"
                        >
                            {availableRegions.map((region) => (
                                <Select.Option
                                    key={region.id}
                                    value={region.id}
                                    title={region.englishName || region.name}
                                >
                                    {region.englishName || region.name}
                                </Select.Option>
                            ))}
                        </Select>
                    )}
                    <AutoComplete
                        getPopupContainer={() => this.searchContainerRef!}
                        autoFocus={!!this.regionSearch}
                        defaultOpen
                        className="gl-header-right-item gl-header-right-item-wrapper certain-category-search search-input"
                        dropdownClassName="certain-category-search-dropdown"
                        dataSource={this.state.searchResult}
                        placeholder={placeHolder}
                        optionLabelProp="title"
                        onChange={this.onChange}
                        onSelect={this.onSelect}
                        {...alignPop()}
                    >
                        <Input.Search
                            ref={(input) => (this.searchElement = input)}
                            onSearch={this.onSearch}
                            suffix={
                                <Icon
                                    type={iconType}
                                    className="certain-category-icon"
                                    onMouseUp={this.onMouseUp}
                                />
                            }
                        />
                    </AutoComplete>
                </Input.Group>
            </div>
        ) : (
            <div className="gl-header-right-item-dropdown">
                <HeaderIcon
                    icon="search"
                    matIcon="search"
                    labelId={GLLocale.Search}
                    onClick={this.beginSearch}
                />
            </div>
        );
    }
}
